import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  createColumnHelper,
  filterFns,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';

import { ColumnConfigPopover } from './column-config-popover';
import { FilterPopover } from './filter-popover';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { PersonSummary } from '@/api/queries/useGetMatterDocPersons';
import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';
import { Doc } from '@/api/types/backend-dto-types';
import { useColumnConfiguration } from '@/hooks/useColumnConfiguration';
import { useDocumentNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { Chip } from '@/new/components/chip';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Popover, PopoverTrigger, PopoverContent } from '@/new/components/shadcn/popover';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { Toggle } from '@/new/components/shadcn/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SortIcon } from '@/new/components/table/sort-icon';
import { TableNullField } from '@/new/components/table/table-null-field';
import { TablePagination } from '@/new/components/table/table-pagination';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import {
  arrInArr,
  includedInArr,
  selectedRowsFilter,
  sortArrayLength,
  sortStringWithEmptyLast,
} from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';
import { useStore } from '@/store';
import { DocFilters } from '@/types';
import { buildClientSideFilterPills, DateRangeFilterValue } from '@/utils/buildFilterPills';

// Extended Doc type with persons
type DocWithPersons = Doc & { persons?: PersonSummary[] };

const fallbackTableData: DocWithPersons[] = [];
const columnHelper = createColumnHelper<DocWithPersons>();

interface TableMeta {
  processingDocIds?: Set<string>;
}

const columns = [
  // Select checkbox - always first
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full items-center justify-center text-center">
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        />
      </div>
    ),
    cell: ({ table, row }) => {
      const { processingDocIds } = (table.options.meta as TableMeta) ?? {};
      const isProcessing = processingDocIds?.has(row.original.id);

      if (isProcessing) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex w-full min-w-5 items-center justify-center text-center">
                <Icon className="lucide--loader-2 h-4 w-4 animate-spin text-amber-500 duration-1500" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>This document is currently being processed</p>
            </TooltipContent>
          </Tooltip>
        );
      }

      return (
        <div className="flex w-full min-w-5 items-center justify-center text-center">
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={row.getToggleSelectedHandler()}
          />
        </div>
      );
    },
    filterFn: selectedRowsFilter,
  }),
  // Filename - always shown
  columnHelper.accessor('file.name', {
    id: 'file.name',
    header: () => <span>Filename</span>,
    cell: (info) => {
      const doc = info.row.original;
      const { openReferenceDrawer } = useStore();

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="line-clamp-1 w-fit break-all text-blue-600 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                openReferenceDrawer({ docId: doc.id });
              }}
            >
              {info.getValue()}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{info.getValue()}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
    sortingFn: sortStringWithEmptyLast,
    meta: {
      sortIcon: 'string',
    },
  }),
  // Checked by default columns
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) =>
      info.getValue() ? (
        <Chip className={cn(CHIP_COLOURS.type)}>
          <div className="line-clamp-1 max-w-28 text-ellipsis">{info.getValue()}</div>
        </Chip>
      ) : (
        <TableNullField />
      ),
    filterFn: includedInArr,
    sortingFn: sortStringWithEmptyLast,
    enableHiding: true,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('file.extension', {
    id: 'file.extension',
    header: () => <span>Extension</span>,
    cell: (info) =>
      info.getValue() ? (
        <Chip className={cn(CHIP_COLOURS.extension)}>
          <div className="line-clamp-1 max-w-28 text-ellipsis">{info.getValue().toUpperCase()}</div>
        </Chip>
      ) : (
        <TableNullField />
      ),
    filterFn: includedInArr,
    enableHiding: true,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('issues', {
    header: () => <span>Issues</span>,
    cell: (info) => {
      const issues = info.getValue();

      if (!issues.length) return <TableNullField />;

      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 py-0.5">
              <span>
                {issues.length} <span className="font-normal">{issues.length === 1 ? 'issue' : 'issues'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {issues.map((issue) => (
                <Chip key={issue} className={cn(CHIP_COLOURS.issue)}>
                  {issue}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    },
    filterFn: arrInArr,
    sortingFn: sortArrayLength,
    enableHiding: true,
    meta: {
      sortIcon: 'number',
    },
  }),
  columnHelper.accessor('persons', {
    id: 'persons',
    header: () => <span>Entities</span>,
    cell: (info) => {
      const persons = info.getValue();

      if (!persons || persons.length === 0) return <TableNullField />;

      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 py-0.5">
              <span>
                {persons.length} <span className="font-normal">{persons.length === 1 ? 'entity' : 'entities'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {persons.map((person) => (
                <Chip key={person.id} className="border-teal-300 bg-teal-100 text-teal-800">
                  {person.name}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    },
    filterFn: (row, columnId, filterValue: string[]) => {
      if (!filterValue || filterValue.length === 0) return true;
      const persons = row.getValue(columnId) as PersonSummary[] | undefined;
      if (!persons || persons.length === 0) return false;
      const personNames = persons.map((p) => p.name);
      return filterValue.some((name) => personNames.includes(name));
    },
    sortingFn: sortArrayLength,
    enableHiding: true,
    meta: {
      sortIcon: 'number',
    },
  }),
  columnHelper.accessor('date', {
    header: () => <span>Document Date</span>,
    cell: (info) =>
      info.getValue() !== null ? (
        <span className="whitespace-nowrap">
          {new Date(info.getValue()!).toLocaleString('en-GB', {
            dateStyle: 'medium',
          })}
        </span>
      ) : (
        <TableNullField />
      ),
    enableHiding: true,
    filterFn: (row, columnId, filterValue: DateRangeFilterValue) => {
      const { startDate, endDate } = filterValue || {};

      if (!startDate && !endDate) return true;

      const docDate = row.getValue(columnId);
      if (!docDate) return false; // Exclude docs without dates when filter active

      const date = new Date(docDate as string);

      if (startDate && date < startDate) return false;

      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (date > endOfDay) return false;
      }

      return true;
    },
  }),
  // Unchecked by default columns
  columnHelper.accessor('author', {
    header: () => <span>Author</span>,
    cell: (info) =>
      info.getValue() ? <div className="line-clamp-1 max-w-64 break-all">{info.getValue()}</div> : <TableNullField />,
    sortingFn: sortStringWithEmptyLast,
    enableHiding: true,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('factsCount', {
    header: () => <span>No. of Facts</span>,
    enableHiding: true,
    meta: {
      sortIcon: 'number',
    },
  }),
  columnHelper.accessor('file.createdDate', {
    id: 'file.createdDate',
    header: () => <span>Run Date/Time</span>,
    cell: (info) =>
      info.getValue() !== null ? (
        <span className="whitespace-nowrap">
          {new Date(info.getValue()!).toLocaleString('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </span>
      ) : (
        <TableNullField />
      ),
    enableHiding: true,
    filterFn: (row, columnId, filterValue: string[]) => {
      if (!filterValue || filterValue.length === 0) return true;

      const uploadDate = row.getValue(columnId);
      if (!uploadDate) return false;

      // Format the row's date to match the filter format
      const formattedDate = new Date(uploadDate as string).toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });

      return filterValue.includes(formattedDate);
    },
    meta: {
      sortIcon: 'string',
    },
  }),
];

// Rename static columns for clarity - dynamic Relativity columns will be added at runtime
const staticColumns = columns;

// Cell renderer for dynamic Relativity fields based on field type
function renderRelativityCell(value: unknown, fieldType: string) {
  if (value === null || value === undefined) return <TableNullField />;

  switch (fieldType) {
    case 'string':
      return <span className="line-clamp-1 max-w-64 break-all">{String(value)}</span>;
    case 'number':
      return <span>{(value as number).toLocaleString()}</span>;
    case 'string[]': {
      if (!Array.isArray(value)) {
        return <span className="line-clamp-1 max-w-64 break-all">{String(value)}</span>;
      }

      const arr = value as string[];
      if (!arr.length) return <TableNullField />;
      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 bg-purple-50 py-0.5 text-purple-700 hover:bg-purple-100">
              <span>
                {arr.length} <span className="font-normal">{arr.length === 1 ? 'value' : 'values'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {arr.map((item, i) => (
                <Chip key={i} className="bg-purple-100/60 text-purple-700">
                  {item}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    }
    case 'number[]': {
      if (!Array.isArray(value)) {
        return <span className="line-clamp-1 max-w-64 break-all">{String(value)}</span>;
      }

      const numArr = value as number[];
      if (!numArr.length) return <TableNullField />;
      return <span>{numArr.join(', ')}</span>;
    }
    default:
      return <span className="line-clamp-1 max-w-64 break-all">{String(value)}</span>;
  }
}

// Custom filter function for Relativity fields - handles both scalar and array values
const relativityFilterFn: FilterFn<DocWithPersons> = (row, columnId, filterValue: string[]) => {
  if (!filterValue || filterValue.length === 0) return true;

  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) return false;

  // Handle array cell values (OR logic - match if any element is in filter)
  if (Array.isArray(cellValue)) {
    return cellValue.some((v) => filterValue.includes(String(v)));
  }

  // Handle scalar cell values
  return filterValue.includes(String(cellValue));
};

// Generate dynamic column definitions from Relativity field mappings
function generateRelativityColumns(fields: FilterField[]) {
  return fields.map((field) => {
    const columnId = `file.fileMetadata.relativityFields.${field.fieldName}`;

    return columnHelper.accessor((row) => row.file?.fileMetadata?.relativityFields?.[field.fieldName], {
      id: columnId,
      header: () => (
        <span className="flex items-center gap-1.5">
          <Icon className="lucide--database h-3 w-3 text-purple-600" />
          {field.displayName}
        </span>
      ),
      cell: (info) => renderRelativityCell(info.getValue(), field.fieldType),
      enableHiding: true,
      filterFn: relativityFilterFn,
      sortingFn:
        field.fieldType === 'number'
          ? 'basic'
          : field.fieldType === 'string[]' || field.fieldType === 'number[]'
            ? sortArrayLength
            : sortStringWithEmptyLast,
      meta: {
        sortIcon: field.fieldType === 'number' || field.fieldType.endsWith('[]') ? 'number' : 'string',
      },
    });
  });
}

// Generate dynamic column definitions from Wexler metadata fields
function generateWexlerColumns(fields: FilterField[]) {
  return fields.map((field) => {
    const columnId = `file.fileMetadata.wexlerFields.${field.fieldName}`;

    return columnHelper.accessor((row) => row.file?.fileMetadata?.wexlerFields?.[field.fieldName], {
      id: columnId,
      header: () => <span>{field.displayName}</span>,
      cell: (info) => renderRelativityCell(info.getValue(), field.fieldType),
      enableHiding: true,
      filterFn: relativityFilterFn,
      sortingFn:
        field.fieldType === 'number'
          ? 'basic'
          : field.fieldType === 'string[]' || field.fieldType === 'number[]'
            ? sortArrayLength
            : sortStringWithEmptyLast,
      meta: {
        sortIcon: field.fieldType === 'number' || field.fieldType.endsWith('[]') ? 'number' : 'string',
      },
    });
  });
}

interface DocPickerTableProps {
  docs: Doc[] | undefined;
  rowSelection: RowSelectionState;
  onRowSelectionChange: Dispatch<SetStateAction<RowSelectionState>>;
  allDocumentsCaseId: string;
  matterId: string;
  mode?: 'create' | 'edit';
  initialDocIds?: string[];
  defaultShowSelected?: boolean;
  isLoadingInitialSelection?: boolean;
  processingDocIds?: Set<string>;
  relativityFields?: FilterField[];
  relativityDistinctValues?: Record<string, string[]>;
  wexlerFields?: FilterField[];
  wexlerDistinctValues?: Record<string, string[]>;
}

export function DocPickerTable({
  docs,
  rowSelection,
  onRowSelectionChange,
  allDocumentsCaseId,
  matterId,
  mode,
  initialDocIds,
  defaultShowSelected,
  isLoadingInitialSelection,
  processingDocIds,
  relativityFields,
  relativityDistinctValues,
  wexlerFields,
  wexlerDistinctValues,
}: DocPickerTableProps) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
    defaultShowSelected ? [{ id: 'select', value: true }] : [],
  );
  const [showSelected, setShowSelected] = useState(defaultShowSelected ?? false);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [isProcessingNL, setIsProcessingNL] = useState(false);
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);

  const docsWithPersons = (docs ?? []) as DocWithPersons[];

  // Column visibility configuration with localStorage persistence
  const { columnVisibility, setColumnVisibility } = useColumnConfiguration(matterId, relativityFields, wexlerFields);

  // Generate dynamic columns and merge with static columns
  const allColumns = useMemo(() => {
    const cols = [...staticColumns];
    if (wexlerFields?.length) {
      cols.push(...generateWexlerColumns(wexlerFields));
    }
    if (relativityFields?.length) {
      cols.push(...generateRelativityColumns(relativityFields));
    }
    return cols;
  }, [relativityFields, wexlerFields]);

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);

  // Check if there are changes from initial selection (edit mode only)
  const hasSelectionChanges = useMemo(() => {
    if (mode !== 'edit' || !initialDocIds) return false;

    const currentIds = Object.keys(rowSelection);
    if (currentIds.length !== initialDocIds.length) return true;

    return !currentIds.every((id) => initialDocIds.includes(id));
  }, [mode, initialDocIds, rowSelection]);

  // Reset selection to initial state
  const handleResetSelection = useCallback(() => {
    if (!initialDocIds) return;

    const initialSelection = initialDocIds.reduce((acc, docId) => {
      acc[docId] = true;
      return acc;
    }, {} as RowSelectionState);

    onRowSelectionChange(initialSelection);
    trackCustomEvent(TrackingEventsV2.SubsetSelectorResetSelection);
  }, [initialDocIds, onRowSelectionChange]);

  // Calling toLocaleString each keypress for each doc causes performacne issues, so precompute
  // for the set of loaded docs
  const docDateStrings = useMemo(() => {
    if (!docs) return new Map<string, string>();

    const dateMap = new Map<string, string>();
    docs.forEach((doc) => {
      if (doc.date) {
        const docDate = new Date(doc.date);
        if (!isNaN(docDate.getTime())) {
          const dateString = docDate.toLocaleString('en-GB', { dateStyle: 'medium' }).toLowerCase();
          const fullMonth = docDate.toLocaleString('en-GB', { month: 'long' }).toLowerCase();
          dateMap.set(doc.id, `${dateString} ${fullMonth}`);
        }
      }
    });
    return dateMap;
  }, [docs]);

  const globalFilterFn: FilterFn<DocWithPersons> = useCallback(
    (row, columnId, filterValue: string) => {
      if (filterFns.includesString(row, columnId, filterValue, () => null)) {
        return true;
      }

      const query = filterValue?.toLowerCase();
      if (query) {
        // Check date strings
        const cachedDateString = docDateStrings.get(row.original.id);
        if (cachedDateString && cachedDateString.includes(query)) {
          return true;
        }

        // Check entity/person names
        const persons = row.original.persons;
        if (persons && persons.length > 0) {
          const personMatch = persons.some((person) => person.name.toLowerCase().includes(query));
          if (personMatch) {
            return true;
          }
        }

        // Check Relativity metadata fields
        const relativityMetadataFields = row.original.file?.fileMetadata?.relativityFields;
        if (relativityMetadataFields) {
          for (const value of Object.values(relativityMetadataFields)) {
            if (value === null || value === undefined) continue;

            if (Array.isArray(value)) {
              // Check each element in array fields
              if (value.some((v) => String(v).toLowerCase().includes(query))) {
                return true;
              }
            } else if (String(value).toLowerCase().includes(query)) {
              return true;
            }
          }
        }

        // Check Wexler metadata fields
        const wexlerMetadataFields = row.original.file?.fileMetadata?.wexlerFields;
        if (wexlerMetadataFields) {
          for (const value of Object.values(wexlerMetadataFields)) {
            if (value === null || value === undefined) continue;

            if (Array.isArray(value)) {
              if (value.some((v) => String(v).toLowerCase().includes(query))) {
                return true;
              }
            } else if (String(value).toLowerCase().includes(query)) {
              return true;
            }
          }
        }
      }

      return false;
    },
    [docDateStrings],
  );

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useDocumentNaturalLanguageFilters({
    caseId: allDocumentsCaseId,
    onFiltersChange: (newFilters: DocFilters) => {
      // Convert DocFilters to ColumnFiltersState
      // TODO: post 2.0 release convert doc NL hook to directly produce column filters
      const filters: ColumnFiltersState = [];

      if (newFilters.documentTypes && newFilters.documentTypes.length > 0) {
        filters.push({ id: 'type', value: newFilters.documentTypes });
      }
      if (newFilters.extensions && newFilters.extensions.length > 0) {
        filters.push({ id: 'file.extension', value: newFilters.extensions });
      }
      if (newFilters.documentThemes && newFilters.documentThemes.length > 0) {
        filters.push({ id: 'issues', value: newFilters.documentThemes });
      }
      if (newFilters.personNames && newFilters.personNames.length > 0) {
        filters.push({ id: 'persons', value: newFilters.personNames });
      }

      // Add date range filter
      if (newFilters.startDate || newFilters.endDate) {
        filters.push({
          id: 'date',
          value: {
            startDate: newFilters.startDate ? new Date(newFilters.startDate) : null,
            endDate: newFilters.endDate ? new Date(newFilters.endDate) : null,
          },
        });
      }

      setColumnFilters(filters);

      // Track filter addition when filters are applied from NL search
      if (filters.length > 0) {
        trackCustomEvent(TrackingEventsV2.AddFilterToSubset);
      }
    },
    onSearchChange: (query: string) => {
      setAppliedSearchQuery(query);
      setSearchQuery(query);
    },
  });

  const onSearchCall = (searchValue: string, useNaturalLanguage: boolean) => {
    if (useNaturalLanguage && searchValue.trim()) {
      setIsProcessingNL(true);
      buildFiltersWithNL(searchValue);
    } else {
      setAppliedSearchQuery(searchValue);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setIsProcessingNL(false);
  };

  // Update processing state based on NL query
  useEffect(() => {
    if (!isProcessingNLFiltersQuery) {
      setIsProcessingNL(false);
    }
  }, [isProcessingNLFiltersQuery]);

  // Build filter pills (including date filters)
  const filterPills = useMemo(() => {
    return buildClientSideFilterPills(columnFilters, setColumnFilters);
  }, [columnFilters]);

  const table = useReactTable({
    data: docsWithPersons ?? fallbackTableData,
    columns: allColumns,
    state: {
      rowSelection,
      columnFilters,
      globalFilter: appliedSearchQuery,
      columnVisibility,
    },
    meta: {
      processingDocIds,
    } as TableMeta,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: (row) => !processingDocIds?.has(row.original.id),
    initialState: {
      pagination: {
        pageSize: 15,
      },
      sorting: [{ id: 'file.name', desc: false }],
    },
    onRowSelectionChange: onRowSelectionChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
  });

  return (
    <section className="flex grow flex-col overflow-hidden px-4" data-tour="doc-picker-table">
      <div className="my-4 flex flex-col gap-2">
        <div className="flex w-full items-center justify-end gap-2">
          <Toggle
            pressed={showSelected}
            onPressedChange={(pressed) => {
              setShowSelected(pressed);
              trackCustomEvent(TrackingEventsV2.SubsetSelectorShowSelectedDocuments);
              const selectColumn = table.getColumn('select');
              if (selectColumn) {
                selectColumn.setFilterValue(pressed ? true : undefined);
              }
            }}
            className="whitespace-nowrap"
            data-tour="show-selected-toggle"
          >
            <Icon className={showSelected ? 'lucide--check-circle-2' : 'lucide--circle'} />
            <span>
              {showSelected ? 'Showing' : 'Show'} {selectedCount} Included{' '}
              {selectedCount === 1 ? 'Document' : 'Documents'}
            </span>
          </Toggle>

          <Popover open={isColumnConfigOpen} onOpenChange={setIsColumnConfigOpen}>
            <PopoverTrigger asChild>
              <Button disabled={!docs}>
                <Icon className="lucide--columns-3" />
                <span>Edit Columns</span>
              </Button>
            </PopoverTrigger>
            <ColumnConfigPopover
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              onClose={() => setIsColumnConfigOpen(false)}
              docs={docsWithPersons}
              relativityFields={relativityFields}
              wexlerFields={wexlerFields}
            />
          </Popover>

          {mode === 'edit' && (
            <Button
              onClick={handleResetSelection}
              disabled={!hasSelectionChanges}
              className="whitespace-nowrap duration-150"
            >
              <Icon className="lucide--rotate-ccw h-3.5 w-3.5" />
              <span>Reset Selection</span>
            </Button>
          )}
        </div>

        <div className="flex gap-2" data-tour="doc-filters">
          <div className="grow">
            <NaturalLanguageSearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              onSearchCall={onSearchCall}
              clearSearch={clearSearch}
              processingNL={isProcessingNL}
              entityType="docs-in-subset-creator"
            />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button disabled={!docs}>
                <Icon className="lucide--funnel-plus" />
                <span>Add Filters</span>
              </Button>
            </PopoverTrigger>
            {docs && (
              <FilterPopover
                docs={docsWithPersons}
                typeColumn={table.getColumn('type')}
                extensionColumn={table.getColumn('file.extension')}
                issuesColumn={table.getColumn('issues')}
                dateColumn={table.getColumn('date')}
                personsColumn={table.getColumn('persons')}
                uploadDateColumn={table.getColumn('file.createdDate')}
                setColumnFilters={setColumnFilters}
                relativityFields={relativityFields}
                relativityDistinctValues={relativityDistinctValues}
                wexlerFields={wexlerFields}
                wexlerDistinctValues={wexlerDistinctValues}
                getColumn={table.getColumn.bind(table)}
              />
            )}
          </Popover>

          <Button
            disabled={!docs}
            onClick={() => {
              setColumnFilters([]);
            }}
          >
            <Icon className="lucide--funnel-x" />
            <span>Clear Filters</span>
          </Button>
        </div>

        <FilterPills pills={filterPills} />
      </div>

      <div className="mt-2 grow overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div
                      className="flex cursor-pointer items-center gap-2 select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon column={header.column} />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoadingInitialSelection ? (
              Array.from({ length: 15 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {table.getVisibleLeafColumns().map((column) => (
                    <TableCell key={`skeleton-${i}-${column.id}`}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={table.getVisibleLeafColumns().length} className="h-64">
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Icon className="lucide--file-x-2 h-5 w-5" />
                      </EmptyMedia>
                      <EmptyTitle>No documents found</EmptyTitle>
                      <EmptyDescription>
                        {appliedSearchQuery || columnFilters.length > 0
                          ? 'Try adjusting your filters or search query'
                          : 'No documents available'}
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isProcessing = processingDocIds?.has(row.original.id);
                return (
                  <TableRow
                    key={row.id}
                    onClick={() => !isProcessing && row.toggleSelected()}
                    className={cn(
                      'cursor-pointer',
                      isProcessing && 'cursor-not-allowed opacity-75',
                      !row.getIsSelected() && 'opacity-60',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination className="mb-4" table={table} plural="documents" />
    </section>
  );
}
